import { useState } from "react";
import { type BrewDetail, send, useChange, useLocations } from "../api";
import { decimalOnly } from "../brewPulls";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { today } from "../format";
import { useNotify } from "../notify";

/**
 * Harvesting yeast (PLAN.md, "Yeast", 2026-10-07): slurry taken from a brewed brew, weighed, as a lot of
 * its strain's harvested yeast, one generation on from what the brew pitched. Without a brew, it is
 * yeast already in a brink when the app arrived, and the brewer says its generation.
 *
 * The generation is how many beers the yeast had fermented before it was harvested: a fresh pitch's first
 * harvest is 1 (G1).
 */
export function HarvestDialog({
  open,
  onClose,
  brew,
  itemId,
  itemName,
}: {
  open: boolean;
  onClose: () => void;
  /** The brew it came out of. */
  brew?: BrewDetail;
  /** Without a brew: the yeast it is, any of its strain. */
  itemId?: string;
  itemName?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="Harvest yeast">
      {open ? (
        <HarvestForm brew={brew} itemId={itemId} itemName={itemName} onDone={onClose} />
      ) : null}
    </Dialog>
  );
}

/** The yeast lines of a brew that took it from inventory, which are what can be harvested. */
export const harvestable = (b: BrewDetail) =>
  b.lines.filter((l) => l.kind === "yeast" && l.item !== null);

/** One generation on from what a line pitched, where every pull of it had a lot to say. */
function nextGeneration(line: BrewDetail["lines"][number]): number | null {
  const pulls = line.pulls;
  if (pulls.length === 0 || pulls.some((p) => p.lotId === null)) return null;
  return Math.max(...pulls.map((p) => p.generation ?? 0)) + 1;
}

function HarvestForm({
  brew,
  itemId,
  itemName,
  onDone,
}: {
  brew?: BrewDetail;
  itemId?: string;
  itemName?: string;
  onDone: () => void;
}) {
  const notify = useNotify();
  const { data: locations = [] } = useLocations();
  const lines = brew ? harvestable(brew) : [];
  const [lineId, setLineId] = useState(lines[0]?.recipeLineId ?? "");
  const line = lines.find((l) => l.recipeLineId === lineId);
  const worked = line ? nextGeneration(line) : null;
  const [generation, setGeneration] = useState(worked === null ? "" : String(worked));
  const [pounds, setPounds] = useState("");
  const [harvestedOn, setHarvestedOn] = useState(today());
  const [bestBy, setBestBy] = useState("");
  // Where its yeast came from, as a good guess at where the brink goes back to.
  const [locationId, setLocationId] = useState(
    line?.pulls.find((p) => p.locationId)?.locationId ?? "",
  );
  const [note, setNote] = useState("");
  const place = locationId || locations[0]?.id || "";
  const name = line?.item?.name ?? itemName ?? "the yeast";

  const harvest = useChange(
    (_: undefined, key) =>
      send.harvest(
        {
          ...(brew ? { brewId: brew.id } : {}),
          ...(line?.item ? { itemId: line.item.id } : itemId ? { itemId } : {}),
          ...(generation ? { generation: Number(generation) } : {}),
          locationId: place,
          quantity: { value: pounds, unit: "lb" },
          harvestedOn,
          ...(bestBy ? { bestBy } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
        key,
      ),
    {
      onSuccess: (done) => {
        notify(
          `Harvested ${pounds} lb of ${name}${done.lot?.generation ? `, G${done.lot.generation}` : ""}`,
        );
        onDone();
      },
    },
  );
  const ready = Number(pounds) > 0 && place !== "" && (brew !== undefined || generation !== "");

  return (
    <DialogForm
      onSubmit={() => ready && harvest.mutate(undefined)}
      footer={
        <>
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={!ready || harvest.isPending}>
            Harvest
          </button>
        </>
      }
    >
      {brew ? (
        <p className="muted">
          From brew {brew.number} of {brew.recipe.name}. It goes into inventory as {name}'s
          harvested yeast, to pitch into another brew.
        </p>
      ) : (
        <p className="muted">
          Yeast already harvested, such as a brink from before the app, goes in as {name}'s
          harvested yeast.
        </p>
      )}
      {lines.length > 1 ? (
        <Field label="Which yeast">
          <select
            className="select"
            value={lineId}
            onChange={(ev) => {
              setLineId(ev.target.value);
              const next = lines.find((l) => l.recipeLineId === ev.target.value);
              const g = next ? nextGeneration(next) : null;
              setGeneration(g === null ? "" : String(g));
            }}
          >
            {lines.map((l) => (
              <option key={l.recipeLineId} value={l.recipeLineId}>
                {l.item?.name ?? l.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="How much" required>
        <span className="input-unit">
          <input
            className="input num"
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            value={pounds}
            onChange={(ev) => setPounds(decimalOnly(ev.target.value))}
          />
          <span className="unit">lb</span>
        </span>
      </Field>
      <Field
        label="Generation"
        required={brew === undefined || worked === null}
        hint={
          worked !== null
            ? `G${worked}: one more than the yeast this brew pitched`
            : brew
              ? "This brew's yeast was not in inventory, so say which generation this is"
              : "How many beers it has fermented: a fresh pitch's first harvest is 1"
        }
      >
        <input
          className="input num"
          inputMode="numeric"
          autoComplete="off"
          value={generation}
          onChange={(ev) => setGeneration(ev.target.value.replace(/\D/g, "").slice(0, 2))}
        />
      </Field>
      <Field label="Harvested on">
        <input
          className="input"
          type="date"
          value={harvestedOn}
          onChange={(ev) => setHarvestedOn(ev.target.value || today())}
        />
      </Field>
      <Field label="Where it is kept">
        <select className="select" value={place} onChange={(ev) => setLocationId(ev.target.value)}>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Pitch by" hint="Optional. Brew day offers it first until this day.">
        <input
          className="input"
          type="date"
          value={bestBy}
          onChange={(ev) => setBestBy(ev.target.value)}
        />
      </Field>
      <Field label="Note">
        <input
          className="input"
          autoComplete="off"
          placeholder="Brink, cell count, anything worth keeping"
          value={note}
          onChange={(ev) => setNote(ev.target.value)}
        />
      </Field>
      <ErrorNote error={harvest.error} />
    </DialogForm>
  );
}
